import { useContext, useEffect, useState } from 'react'
import {
    Box,
    CircularProgress,
    Container,
    Grid,
    IconButton,
    Typography,
} from '@mui/material'
import { Refresh } from '@mui/icons-material'
import { Helmet } from 'react-helmet'
import { api } from '../../../services/Main/Api'
import { getCondoId } from '../../../services/Main/AuthStorage'
import Context from '../../../contexts/Context'
import BalanceCard from './components/BalanceCard'
import QuickActions from './components/QuickActions'
import RecentTransactions from './components/RecentTransactions'
import AccountsOverview from './components/AccountsOverview'

function mapBankEntry(item) {
    const id = item.id ?? item.entry_id ?? item.EntryId ?? item.EntryID ?? `${Math.random()}`

    const dateRaw =
        item.entry_date ?? item.date ?? item.transactionDate ?? item.created_at ?? item.Date ?? item.PostingDate

    let iso = dateRaw
    try {
        const d = new Date(dateRaw)
        if (!isNaN(d.getTime())) iso = d.toISOString()
    } catch (e) { /* keep raw */ }

    const desc =
        item.details ?? item.description ?? item.history ?? item.comment ?? item.counterPartyName ?? 'Transacao'

    const nature = (item.nature ?? item.type ?? item.Nature ?? item.Type ?? '').toString().toUpperCase()
    const isCredit = nature.includes('CREDIT') || nature.includes('CREDITO')
    const isDebit = nature.includes('DEBIT') || nature.includes('DEBITO')

    let v = item.entry_value ?? item.value ?? item.amount ?? item.Amount ?? 0
    v = Number(v) || 0

    if ('creditValue' in item && Number(item.creditValue)) v = Number(item.creditValue)
    if ('debitValue' in item && Number(item.debitValue)) v = -Math.abs(Number(item.debitValue))
    if (!('debitValue' in item) && !('creditValue' in item) && (isCredit || isDebit)) {
        v = Math.abs(v) * (isDebit ? -1 : 1)
    }
    if (Number.isInteger(v) && Math.abs(v) > 100000 && Math.abs(v) % 10 === 0) {
        v = v / 100
    }

    return { id, data: iso, descricao: desc, valor: v }
}

const BankHub = () => {
    const { condoIdLogin } = useContext(Context)
    const [loading, setLoading] = useState(true)
    const [accountInfo, setAccountInfo] = useState(null)
    const [balance, setBalance] = useState(null)
    const [entries, setEntries] = useState([])
    const [accounts, setAccounts] = useState([])

    const fetchData = () => {
        const condoId = condoIdLogin ?? getCondoId()
        if (!condoId) {
            setLoading(false)
            return
        }

        setLoading(true)

        const today = new Date()
        const thirtyDaysAgo = new Date()
        thirtyDaysAgo.setDate(today.getDate() - 30)
        const fmtDate = (d) => {
            const yyyy = d.getFullYear()
            const mm = String(d.getMonth() + 1).padStart(2, '0')
            const dd = String(d.getDate()).padStart(2, '0')
            return `${yyyy}-${mm}-${dd}`
        }

        const promises = [
            api.get(`fitbank/${condoId}/`).catch(() => ({ data: null })),
            api.get(`fitbank/balance/${condoId}/`).catch(() => ({ data: null })),
            api
                .get('/fitbank/bank-statement-entry/', {
                    params: {
                        condo_id: condoId,
                        date_from: fmtDate(thirtyDaysAgo),
                        date_to: fmtDate(today),
                        page: 1,
                    },
                })
                .catch(() => ({ data: { results: [] } })),
            api
                .get('cashflow/managed-bank-accounts/', {
                    params: { condo_id: getCondoId(), is_active: true },
                })
                .catch(() => ({ data: [] })),
        ]

        Promise.all(promises)
            .then(([accRes, balRes, stmtRes, accsRes]) => {
                // Account info
                if (accRes.data) {
                    setAccountInfo({
                        bank: {
                            name: 'Fitbank',
                            code: accRes.data.spb_bank,
                        },
                        bank_branch: accRes.data.spb_bank_branch,
                        bank_account: `${accRes.data.spb_bank_account}${
                            accRes.data.spb_bank_account_digit
                                ? ` - ${accRes.data.spb_bank_account_digit}`
                                : ''
                        }`,
                        pix_key_company: accRes.data.pix_key_company,
                    })
                }

                // Balance
                if (balRes.data) {
                    setBalance(balRes.data.balance ?? balRes.data.Balance ?? 0)
                }

                // Recent entries
                const rawEntries = stmtRes.data?.results || stmtRes.data || []
                const mapped = rawEntries.map(mapBankEntry)
                mapped.sort((a, b) => new Date(b.data) - new Date(a.data))
                setEntries(mapped.slice(0, 10))

                // Managed accounts
                setAccounts(accsRes.data?.results || accsRes.data || [])
            })
            .finally(() => setLoading(false))
    }

    useEffect(() => {
        fetchData()
    }, [condoIdLogin])

    return (
        <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh' }}>
            <Helmet>
                <title>Banco - Bloco B3</title>
            </Helmet>

            <Container maxWidth="xl" sx={{ py: 3 }}>
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 3,
                        flexWrap: 'wrap',
                        gap: 2,
                    }}
                >
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1E293B' }}>
                            Banco
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748B', mt: 0.5 }}>
                            Gerencie suas operacoes bancarias
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={fetchData}
                        disabled={loading}
                        sx={{
                            bgcolor: '#fff',
                            border: '1px solid #E2E8F0',
                            '&:hover': { bgcolor: '#F1F5F9' },
                        }}
                    >
                        <Refresh sx={{ color: '#64748B' }} />
                    </IconButton>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                        <CircularProgress sx={{ color: '#3B82F6' }} />
                    </Box>
                ) : (
                    <>
                        {/* Row 1: Balance + Quick Actions */}
                        <Grid container spacing={2.5} sx={{ mb: 3 }}>
                            <Grid item xs={12} md={7}>
                                <BalanceCard accountInfo={accountInfo} balance={balance} />
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <QuickActions />
                            </Grid>
                        </Grid>

                        {/* Row 2: Recent Transactions */}
                        <Box sx={{ mb: 3 }}>
                            <RecentTransactions entries={entries} />
                        </Box>

                        {/* Row 3: Accounts Overview */}
                        <AccountsOverview accounts={accounts} />
                    </>
                )}
            </Container>
        </Box>
    )
}

export default BankHub
